import React, {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {Link} from 'react-router-dom';
import {Bell} from 'lucide-react';
import Modal from './Modal';

export const NOTIFICATION_REFRESH_MS = 30000;

export default function Notifications() {
  const [feed, setFeed] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const active = useRef(false);
  const controller = useRef(null);
  const generation = useRef(0);
  const busy = useRef(false);
  const load = useCallback(async () => {
    if (busy.current) return;
    const version = ++generation.current;
    busy.current = true; controller.current = new AbortController();
    try {
      const {data} = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/feed?offset=${offset}`, {timeout:15000, signal:controller.current.signal});
      if (active.current && version === generation.current) {setFeed(data); setError('');}
    } catch (failure) {
      if (active.current && version === generation.current && failure.code !== 'ERR_CANCELED') setError('Bildirimler güncellenemedi. Tekrar deneyebilirsin.');
    } finally {if (version === generation.current) busy.current = false;}
  }, [offset]);
  useEffect(() => {
    active.current = true; busy.current = false; load();
    const refresh = () => {if (document.visibilityState === 'visible') load();};
    // A11: arka plan yenilemesi 30 sn (5 sn gereksiz yük oluşturuyordu); sekmeye dönünce, odakta,
    // zile tıklayınca ve başvuru değişince anında yenilenir.
    const timer = setInterval(refresh, NOTIFICATION_REFRESH_MS);
    document.addEventListener('visibilitychange', refresh); window.addEventListener('focus', refresh);
    window.addEventListener('applications-changed', refresh);
    // This is a request generation counter, not a DOM ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {active.current = false; ++generation.current; controller.current?.abort(); busy.current = false; clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); window.removeEventListener('applications-changed', refresh);};
  }, [load]);
  const read = async item => {
    setOpen(false);
    if (item.read) return;
    ++generation.current; controller.current?.abort(); busy.current = false;
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${encodeURIComponent(item.id)}/read`, {}, {timeout:15000});
      if (active.current) setFeed(current => current && ({...current, unread_count:Math.max(0,current.unread_count - 1), items:current.items.map(n => n.id === item.id ? {...n,read:true} : n)}));
    } catch {if (active.current) setError('Bildirim açıldı ancak okundu bilgisi kaydedilemedi. Yeniden açarak deneyebilirsin.');}
  };
  const unread = feed?.unread_count || 0;
  const items = tab === 'pending' ? feed?.pending || [] : feed?.items || [];
  return <>
    <button type="button" className="icon-button notification-bell" aria-label={`Bildirimler, ${unread} okunmamış`} onClick={() => {setOpen(true); load();}}>
      <Bell size={21}/>{unread > 0 && <span className="notification-count">{unread > 99 ? '99+' : unread}</span>}
    </button>
    <Modal open={open} onClose={() => setOpen(false)} title="Bildirimler">
      <div className="application-buttons notification-tabs" role="group" aria-label="Bildirim görünümü">
        <button className="action-secondary" aria-pressed={tab === 'pending'} onClick={() => {setTab('pending');setOffset(0);}}>Yanıt bekleyenler ({feed?.pending_count || 0})</button>
        <button className="action-secondary" aria-pressed={tab === 'all'} onClick={() => {setTab('all');setOffset(0);}}>Tüm bildirimler</button>
      </div>
      {error && <p role="alert">{error} <button className="text-action" onClick={load}>Tekrar dene</button></p>}
      {!feed && !error ? <p role="status">Bildirimler yükleniyor…</p> : items.length ? <ul className="notification-items">{items.map(item => <li key={item.id} className={tab === 'all' && !item.read ? 'notification-unread' : ''}>
        <Link to={item.path} onClick={() => tab === 'all' ? read(item) : setOpen(false)}>
          {item.event_type === 'profile_review' && <span className="status-pill">Profil incelemesi</span>}
          {item.event_type === 'profile_reminder' && <span className="status-pill">Profil tamamlama</span>}
          {item.event_type === 'mutual_match' && <span className="status-pill">Karşılıklı eşleşme</span>}
          {item.event_type === 'reminder48' && <span className="status-pill">Hatırlatma</span>}
          {['sanction', 'sanction_lifted'].includes(item.event_type) && <span className="status-pill">Hesap bildirimi</span>}
          <strong>{item.title}</strong>{item.details && <p>{item.details}</p>}
          <span className="text-action">{tab === 'pending' ? 'Talebi yanıtla' : item.action_label || 'Talebi aç'}</span>
        </Link>
        {item.created_at && <time>{new Date(item.created_at).toLocaleString('tr-TR')}</time>}
      </li>)}</ul> : feed && <p>{tab === 'pending' ? 'Şu anda yanıtını bekleyen bir talep yok.' : 'Henüz bildirimin yok.'}</p>}
      <div className="application-buttons">
        {offset > 0 && <button className="action-secondary" onClick={() => setOffset(Math.max(0,offset - 20))}>Önceki</button>}
        {(tab === 'pending' ? feed?.pending_has_more : feed?.has_more) && <button className="action-secondary" onClick={() => setOffset(offset + 20)}>Sonraki</button>}
      </div>
      <p className="text-xs text-zinc-500 mt-3">Bir bildirimi okumak, talebi yanıtlamak anlamına gelmez.</p>
    </Modal>
  </>;
}
